import { useInitiateChat } from 'app/core/api/repairer/api-repairer';
import { RepairerSearchItem } from 'app/core/api/repairer/api-repairer.types';
import styledFuncs from 'app/styles/styledFuncs';
import { Opt } from 'app/tools/general.types';
import {
  useObservableSend,
  useWatchAsRef,
} from 'app/tools/hooks/misc-hooks';
import React, { useEffect, useRef, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import { CarouselProps } from 'react-responsive-carousel/lib/ts/components/Carousel/types';
import styled from 'styled-components/macro';
import SlideView, { SwipeActions } from './SliderView';

const RepairerContainer = styled.div`
  position: absolute;
  width: 100%;
  z-index: 1;
  bottom: ${styledFuncs.pad(2)};
`;

type RepairerProps = {
  repairers: RepairerSearchItem[];
  selectedRepairer: RepairerSearchItem;
  setSelectedRepairer: (repairer: RepairerSearchItem) => void;
};
export default function RepairerDetails({
  repairers,
  selectedRepairer,
  setSelectedRepairer,
}: RepairerProps): JSX.Element {
  const [currentSlide, setCurrentSlide] = useState(0);
  const currentSlideRef = useWatchAsRef(currentSlide);
  const changingRepairerEnabled = useRef(true);
  const slideIsExpanded = useRef(false);
  const listeningForVerticalSwipe = useRef(false);
  const swipeIsInProgress = useRef(false);

  /**
   * Ref holding whether the swipe is horizontal (indicating moving carousel, so
   * value is `true`) or vertical (indicating expanding / contracting slide, so
   * value is `false` as it is not moving the carousel).
   * Value is undefined if the distinction has not been made yet.
   */
  const swipeMovesCarousel = useRef<Opt<boolean>>(undefined);

  const [sendSwiped, swiped$] = useObservableSend<SwipeActions>();

  const runActionOnSlide = (action: 'expand' | 'collapse') => {
    if (action === 'expand' && !slideIsExpanded.current) {
      sendSwiped('up');
      slideIsExpanded.current = true;
      return true;
    }
    if (action === 'collapse' && slideIsExpanded.current) {
      sendSwiped('down');
      slideIsExpanded.current = false;
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!changingRepairerEnabled.current) {
      setSelectedRepairer(repairers[currentSlideRef.current]);
      return;
    }
    const selRepairerIdx = repairers.indexOf(selectedRepairer);
    setCurrentSlide(selRepairerIdx === -1 ? 0 : selRepairerIdx);
  }, [
    currentSlideRef,
    repairers,
    selectedRepairer,
    setSelectedRepairer,
  ]);

  const { sending: initiatingChat } = useInitiateChat();
  useEffect(() => {
    changingRepairerEnabled.current = !initiatingChat;
  }, [initiatingChat]);

  const handleSliderChange = (cSlide: number) => {
    setCurrentSlide(cSlide);
    setSelectedRepairer(repairers[cSlide]);
  };

  /**
   * Handles the mouse/touch swipe events from onSwipeMove on Carousel
   * @param event MouseEvent or TouchEvent
   * @param delta x, y distance mouse/touch has swiped, in px
   * @returns value determining if swipe movement is allowed or adjusted:
   * - `void` or `true` -> swipe goes ahead unaffected
   * - `false` -> swipe is blocked from happening
   * - `{ x, y }` -> swipe delta is adjusted to the returned x and y values
   */
  const handleSwipeMove: CarouselProps['onSwipeMove'] = (
    event,
    { x, y } = { x: NaN, y: NaN }
  ) => {
    // if selected repairer is locked (eg. if loading starting chat),
    // return false to prevent swipe from being registered.
    if (!changingRepairerEnabled.current) return false;
    // if x is not a number, delta was missing so just return (leave as is)
    // this happened once and caused an error, but seems very rare / one-off
    if (Number.isNaN(x)) return;

    // if the swipe is happening, stop it registering as a normal drag on the
    // page. On mobile this would have caused the page to try and scroll.
    event.preventDefault();

    // if swipe is vertical and vertical swipes are still being listened for
    if (
      swipeMovesCarousel.current === false &&
      listeningForVerticalSwipe.current
    ) {
      swipeIsInProgress.current = true;
      const actioned = runActionOnSlide(
        y > 0 ? 'collapse' : 'expand'
      );
      // if the vertical swipe caused an expand or collapse action,
      // stop listening for a vertical swipe.
      if (actioned) listeningForVerticalSwipe.current = false;
    }

    if (swipeMovesCarousel.current !== undefined) {
      // if swipe is already recognised as vertical or horizontal, return
      // immediately with the modified swipe movement. This means vertical
      // swipes don't cause horizontal movement of the carousel.
      const rtn = {
        x: x * Number(swipeMovesCarousel.current),
        y,
      };
      return rtn;
    }

    // work out the gradient and distance of the drag movement.
    const gradient = Math.abs(y) / Math.abs(x);
    const distance = (y ** 2 + x ** 2) ** 0.5;
    const carouselIsMoving = gradient < 1; // otherwise is a vertical swipe

    // lock action to either h or v when distance is greater than 7px;
    if (distance > 7) {
      swipeMovesCarousel.current = carouselIsMoving;
    }
    // void return means that the swipe is unaffected
  };

  const handleSwiped: CarouselProps['onSwiped'] = () =>
    changingRepairerEnabled.current && swipeMovesCarousel.current;

  const handleClickItem: CarouselProps['onClickItem'] = i => {
    if (!changingRepairerEnabled.current) return false;
    if (i === currentSlide && !swipeIsInProgress.current) {
      runActionOnSlide('expand');
    }
  };

  useEffect(() => {
    if (slideIsExpanded.current) sendSwiped('up');
  }, [sendSwiped, currentSlide]);

  return (
    <RepairerContainer>
      <Carousel
        emulateTouch
        swipeable
        showThumbs={false}
        infiniteLoop={false}
        swipeScrollTolerance={5}
        preventMovementUntilSwipeScrollTolerance
        centerMode
        centerModeEndSpacing
        centerSlidePercentage={80}
        selectedItem={currentSlide}
        onChange={handleSliderChange}
        onSwipeMove={handleSwipeMove}
        onSwiped={handleSwiped}
        onClickItem={handleClickItem}
        onSwipeStart={() => {
          listeningForVerticalSwipe.current = true;
          swipeMovesCarousel.current = undefined;
        }}
        onSwipeEnd={() => {
          listeningForVerticalSwipe.current = false;
          setTimeout(() => {
            swipeIsInProgress.current = false;
          }, 100);
        }}
        showIndicators={false}
        showStatus={false}
        showArrows={false}
        autoPlay={false}
      >
        {repairers.map((repairer, i) => (
          <SlideView
            key={repairer.id}
            repairer={repairer}
            isCurrentSlide={i === currentSlide}
            {...{ swiped$ }}
          />
        ))}
      </Carousel>
    </RepairerContainer>
  );
}
